import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  Checkbox,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  Folder,
  InsertDriveFile,
  Refresh,
  Delete,
  MoreVert,
} from '@mui/icons-material';
import { IFileListItem } from '../../client/SmartAssistantBackendClient';

type FileTreeNode = IFileListItem & {
  id: string;
  path: string;
  children?: FileTreeNode[];
  expanded?: boolean;
  loading?: boolean;
  error?: string | null;
  action?: 'embedding' | 'deleting' | 'creating' | 'renaming' | 'uploading' | null;
};

type Props = {
  rootKey?: string;
  loadChildren: (path: string | null) => Promise<IFileListItem[]>;
  onEmbed: (item: IFileListItem) => Promise<{ docId?: string } | void>;
  onDelete: (item: IFileListItem) => Promise<void>;
  onCreateFolder?: (parentPath: string | null, folderName: string) => Promise<void>;
  onRenameFolder: (sourcePath: string, targetPath: string) => Promise<void>;
  onDeleteFolder: (folderPath: string) => Promise<void>;
  onUploadToFolder: (folderPath: string | null, files: File[]) => Promise<void>;
};

const toDisplayName = (item: FileTreeNode) => {
  const explicitName = (item.fileName ?? '').trim();
  if (explicitName) return explicitName;
  const source = item.blobPath || item.path || item.fileUrl;
  if (source) {
    const parts = source.split('/').filter(Boolean);
    if (parts.length > 0) return parts[parts.length - 1];
    return source;
  }
  return 'Unbekannt';
};

const normalizePath = (path: string | undefined, name?: string) => {
  const clean = (path ?? '').replace(/^\/+|\/+$/g, '');
  if (!clean && name) return name;
  if (clean && name) return `${clean}/${name}`;
  return clean;
};

const getParentPath = (path: string | null | undefined): string | null => {
  const clean = (path ?? '').replace(/^\/+|\/+$/g, '');
  if (!clean) return null;
  const parts = clean.split('/');
  parts.pop();
  const parent = parts.join('/');
  return parent || null;
};

const buildNodes = (items: IFileListItem[], parentPath: string): FileTreeNode[] =>
  items.map((item, index) => {
    const blobPath = (item.blobPath ?? '').replace(/^\/+|\/+$/g, '');
    const path = blobPath || normalizePath(parentPath, item.fileName);
    const id = path || item.fileUrl || `${parentPath || 'root'}-${index}`;
    return {
      ...item,
      id,
      path,
      expanded: false,
      loading: false,
      error: null,
      action: null,
      children: item.isFolder ? [] : undefined,
    };
  });

const mapTree = (
  nodes: FileTreeNode[],
  path: string,
  updater: (node: FileTreeNode) => FileTreeNode
): FileTreeNode[] =>
  nodes.map((n) => {
    if (n.path === path) return updater(n);
    if (n.children) {
      const updatedChildren = mapTree(n.children, path, updater);
      if (updatedChildren !== n.children) {
        return { ...n, children: updatedChildren };
      }
    }
    return n;
  });

const removeFromTree = (nodes: FileTreeNode[], path: string): FileTreeNode[] => {
  const filtered = nodes.filter((n) => n.path !== path);
  if (filtered.length !== nodes.length) return filtered;
  return nodes.map((n) => {
    if (!n.children) return n;
    const updatedChildren = removeFromTree(n.children, path);
    if (updatedChildren !== n.children) return { ...n, children: updatedChildren };
    return n;
  });
};

const findNodeByPath = (nodes: FileTreeNode[], path: string): FileTreeNode | null => {
  for (const n of nodes) {
    if (n.path === path) return n;
    if (n.children) {
      const found = findNodeByPath(n.children, path);
      if (found) return found;
    }
  }
  return null;
};

const EmptyState: React.FC = () => (
  <Box
    sx={(theme) => ({
      border: `1px dashed ${theme.palette.divider}`,
      borderRadius: 2,
      p: 2,
      textAlign: 'center',
      color: theme.palette.text.secondary,
    })}
  >
    Noch keine Dateien geladen.
  </Box>
);

export const FileTree: React.FC<Props> = ({
  rootKey,
  loadChildren,
  onEmbed,
  onDelete,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onUploadToFolder,
}) => {
  const loadChildrenRef = React.useRef(loadChildren);
  const [nodes, setNodes] = useState<FileTreeNode[]>([]);
  const [rootLoading, setRootLoading] = useState<boolean>(false);
  const [rootError, setRootError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [folderMenu, setFolderMenu] = useState<{ path: string; anchorEl: HTMLElement | null } | null>(null);

  useEffect(() => {
    loadChildrenRef.current = loadChildren;
  }, [loadChildren]);

  const loadRoot = useCallback(async () => {
    setRootLoading(true);
    setRootError(null);
    setSelected(new Set());
    try {
      const items = await loadChildrenRef.current(null);
      setNodes(buildNodes(items ?? [], ''));
    } catch (err: any) {
      setRootError(err?.message ?? 'Konnte Wurzelordner nicht laden.');
    } finally {
      setRootLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoot();
  }, [loadRoot, rootKey]);

  const refreshFolder = useCallback(async (path: string | null) => {
    const normalized = (path ?? '').replace(/^\/+|\/+$/g, '');
    if (!normalized) {
      await loadRoot();
      return;
    }
    setNodes((prev) =>
      mapTree(prev, normalized, (node) => ({
        ...node,
        loading: true,
        error: null,
      }))
    );
    try {
      const items = await loadChildrenRef.current(normalized);
      const children = buildNodes(items ?? [], normalized);
      setNodes((prev) =>
        mapTree(prev, normalized, (node) => ({
          ...node,
          loading: false,
          expanded: true,
          children,
        }))
      );
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, normalized, (node) => ({
          ...node,
          loading: false,
          error: err?.message ?? 'Fehler beim Laden',
        }))
      );
    }
  }, [loadRoot]);

  const expandFolder = useCallback(async (path: string) => {
    setNodes((prev) => mapTree(prev, path, (node) => ({ ...node, loading: true, error: null })));
    try {
      const items = await loadChildren(path || null);
      const children = buildNodes(items ?? [], path);
      setNodes((prev) =>
        mapTree(prev, path, (node) => ({
          ...node,
          expanded: true,
          loading: false,
          children,
        }))
      );
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, path, (node) => ({
          ...node,
          expanded: true,
          loading: false,
          error: err?.message ?? 'Fehler beim Laden',
        }))
      );
    }
  }, [loadChildren]);

  const collapseFolder = useCallback((path: string) => {
    setNodes((prev) =>
      mapTree(prev, path, (node) => ({
        ...node,
        expanded: false,
      }))
    );
  }, []);

  const handleToggle = useCallback((node: FileTreeNode) => {
    if (!node.isFolder) return;
    if (node.expanded) {
      collapseFolder(node.path);
    } else {
      expandFolder(node.path);
    }
  }, [collapseFolder, expandFolder]);

  const handleEmbed = useCallback(async (node: FileTreeNode) => {
    const target = node.fileUrl ?? node.blobPath;
    if (!target) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({ ...n, error: 'Keine URL zum Einbetten vorhanden.' }))
      );
      return;
    }
    setNodes((prev) =>
      mapTree(prev, node.path, (n) => ({ ...n, action: 'embedding', error: null }))
    );
    try {
      const result = await onEmbed(node);
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({
          ...n,
          embedded: true,
          docId: result?.docId ?? n.docId,
          action: null,
        }))
      );
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({
          ...n,
          action: null,
          error: err?.message ?? 'Einbetten fehlgeschlagen',
        }))
      );
    }
  }, [onEmbed]);

  const handleDelete = useCallback(async (node: FileTreeNode) => {
    const target = node.fileUrl ?? node.blobPath;
    if (!target) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({ ...n, error: 'Keine URL zum Löschen vorhanden.' }))
      );
      return;
    }
    if (typeof window !== 'undefined' && !window.confirm('Datei wirklich löschen?')) {
      return;
    }
    setNodes((prev) =>
      mapTree(prev, node.path, (n) => ({ ...n, action: 'deleting', error: null }))
    );
    try {
      await onDelete(node);
      setNodes((prev) => removeFromTree(prev, node.path));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(node.path);
        return next;
      });
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({
          ...n,
          action: null,
          error: err?.message ?? 'Löschen fehlgeschlagen',
        }))
      );
    }
  }, [onDelete]);

  const handleCreateFolder = useCallback(async (parent: FileTreeNode | null) => {
    if (!onCreateFolder) return;
    const input = typeof window === 'undefined' ? '' : window.prompt('Neuer Ordnername', '');
    const name = (input ?? '').trim().replace(/^\/+|\/+$/g, '');
    if (!name) return;
    const parentPath = parent?.path ?? null;
    if (!parentPath) {
      setRootError(null);
    }
    if (parent) {
      setNodes((prev) => mapTree(prev, parent.path, (n) => ({ ...n, action: 'creating', error: null })));
    }
    try {
      await onCreateFolder(parentPath, name);
      await refreshFolder(parentPath);
    } catch (err: any) {
      if (parent) {
        setNodes((prev) =>
          mapTree(prev, parent.path, (n) => ({
            ...n,
            action: null,
            error: err?.message ?? 'Anlegen fehlgeschlagen',
          }))
        );
      } else {
        setRootError(err?.message ?? 'Anlegen fehlgeschlagen');
      }
      return;
    }
    if (parent) {
      setNodes((prev) => mapTree(prev, parent.path, (n) => ({ ...n, action: null })));
    }
  }, [onCreateFolder, refreshFolder]);

  const handleRenameFolder = useCallback(async (node: FileTreeNode) => {
    const currentName = node.path.split('/').pop() ?? node.path;
    const input = typeof window === 'undefined' ? '' : window.prompt('Neuer Ordnername', currentName);
    const nextName = (input ?? '').trim().replace(/^\/+|\/+$/g, '');
    if (!nextName || nextName === currentName) return;
    const parentPath = getParentPath(node.path);
    const targetPath = normalizePath(parentPath ?? '', nextName);
    setNodes((prev) => mapTree(prev, node.path, (n) => ({ ...n, action: 'renaming', error: null })));
    try {
      await onRenameFolder(node.path, targetPath);
      setSelected((prev) => {
        if (!prev.has(node.path)) return prev;
        const next = new Set(prev);
        next.delete(node.path);
        next.add(targetPath);
        return next;
      });
      await refreshFolder(parentPath);
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({
          ...n,
          action: null,
          error: err?.message ?? 'Umbenennen fehlgeschlagen',
        }))
      );
      return;
    }
    setNodes((prev) => mapTree(prev, node.path, (n) => ({ ...n, action: null })));
  }, [onRenameFolder, refreshFolder]);

  const toggleSelect = useCallback((node: FileTreeNode) => {
    if (node.isFolder) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(node.path)) {
        next.delete(node.path);
      } else {
        next.add(node.path);
      }
      return next;
    });
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (selected.size === 0) return;
    if (typeof window !== 'undefined' && !window.confirm(`Wirklich ${selected.size} Dateien löschen?`)) {
      return;
    }
    const targets = Array.from(selected);
    const targetNodes = targets
      .map((path) => findNodeByPath(nodes, path))
      .filter((n): n is FileTreeNode => !!n && !n.isFolder);

    // Mark all as deleting
    setNodes((prev) => {
      let next = prev;
      selected.forEach((path) => {
        next = mapTree(next, path, (n) => ({ ...n, action: 'deleting', error: null }));
      });
      return next;
    });

    const promises = targetNodes.map((node) =>
      onDelete(node)
        .then(() => {
          setNodes((prev) => removeFromTree(prev, node.path));
          setSelected((prev) => {
            const next = new Set(prev);
            next.delete(node.path);
            return next;
          });
        })
        .catch((err: any) => {
          setNodes((prev) =>
            mapTree(prev, node.path, (n) => ({
              ...n,
              action: null,
              error: err?.message ?? 'Löschen fehlgeschlagen',
            }))
          );
        })
    );

    await Promise.all(promises);
  }, [nodes, onDelete, selected]);

  const collectFilePaths = useCallback((node: FileTreeNode | undefined): string[] => {
    if (!node) return [];
    if (!node.isFolder) return node.path ? [node.path] : [];
    return (node.children ?? []).flatMap((child) => collectFilePaths(child));
  }, []);

  const toggleFolderSelection = useCallback((node: FileTreeNode) => {
    const paths = collectFilePaths(node);
    if (paths.length === 0) return;
    const allSelected = paths.every((p) => selected.has(p));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        paths.forEach((p) => next.delete(p));
      } else {
        paths.forEach((p) => next.add(p));
      }
      return next;
    });
  }, [collectFilePaths, selected]);

  const handleDeleteFolder = useCallback(async (node: FileTreeNode) => {
    const confirmed = typeof window === 'undefined' || window.confirm('Ordner inkl. Dateien löschen?');
    if (!confirmed) return;
    setNodes((prev) => mapTree(prev, node.path, (n) => ({ ...n, action: 'deleting', error: null })));
    try {
      await onDeleteFolder(node.path);
      const filePaths = collectFilePaths(node);
      setSelected((prev) => {
        const next = new Set(prev);
        filePaths.forEach((p) => next.delete(p));
        return next;
      });
      setNodes((prev) => removeFromTree(prev, node.path));
    } catch (err: any) {
      setNodes((prev) =>
        mapTree(prev, node.path, (n) => ({
          ...n,
          action: null,
          error: err?.message ?? 'Löschen fehlgeschlagen',
        }))
      );
    }
  }, [collectFilePaths, onDeleteFolder]);

  const handleUploadToFolder = useCallback(async (node: FileTreeNode | null, files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (node) {
      setNodes((prev) => mapTree(prev, node.path, (n) => ({ ...n, action: 'uploading', error: null })));
    }
    try {
      await onUploadToFolder(node?.path ?? null, Array.from(files));
      await refreshFolder(node?.path ?? null);
    } catch (err: any) {
      if (node) {
        setNodes((prev) =>
          mapTree(prev, node.path, (n) => ({
            ...n,
            action: null,
            error: err?.message ?? 'Upload fehlgeschlagen',
          }))
        );
      } else {
        setRootError(err?.message ?? 'Upload fehlgeschlagen');
      }
      return;
    }
    if (node) {
      setNodes((prev) => mapTree(prev, node.path, (n) => ({ ...n, action: null })));
    }
  }, [onUploadToFolder, refreshFolder]);

  const openFolderMenu = useCallback((event: React.MouseEvent<HTMLElement>, node: FileTreeNode) => {
    event.stopPropagation();
    setFolderMenu({ path: node.path, anchorEl: event.currentTarget });
  }, []);

  const closeFolderMenu = useCallback(() => setFolderMenu(null), []);

  const renderNode = useCallback((node: FileTreeNode, depth: number) => {
    const isFolder = node.isFolder;
    const disabled = !!node.action || node.loading;
    const isSelected = selected.has(node.path);
    const folderFilePaths = isFolder ? collectFilePaths(node) : [];
    const allSelectedInFolder = folderFilePaths.length > 0 && folderFilePaths.every((p) => selected.has(p));

    return (
      <React.Fragment key={node.id}>
        <ListItem
          disableGutters
          sx={{
            pl: 1 + depth * 2,
            pr: 1,
            py: 0.5,
            alignItems: 'flex-start',
            borderRadius: 1,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' },
          }}
        >
          <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
            {isFolder ? (
              <IconButton
                size="small"
                onClick={() => handleToggle(node)}
                disabled={node.loading}
                sx={{ color: 'text.secondary' }}
              >
                {node.expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Checkbox
                size="small"
                checked={isSelected}
                onChange={() => toggleSelect(node)}
                disabled={disabled}
              />
            )}
            <Box display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
              {isFolder ? (
                <Folder fontSize="small" color="primary" />
              ) : (
                <InsertDriveFile fontSize="small" color="action" />
              )}
              <ListItemText
                primary={
                  <Typography noWrap fontWeight={600}>
                    {toDisplayName(node)}
                  </Typography>
                }
                secondary={
                  !node.isFolder ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                      <Chip
                        size="small"
                        label={node.embedded ? 'Embedded' : 'Nicht embedded'}
                        color={node.embedded ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                      {node.docId && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          DocId: {node.docId}
                        </Typography>
                      )}
                      {node.error && (
                        <Typography variant="caption" color="error" noWrap>
                          {node.error}
                        </Typography>
                      )}
                    </Stack>
                  ) : undefined
                }
                primaryTypographyProps={{ fontSize: 14 }}
              />
            </Box>
          </Box>

          {isFolder ? (
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0} justifyContent="flex-end">
              <Button
                size="small"
                variant="text"
                onClick={() => toggleFolderSelection(node)}
                disabled={folderFilePaths.length === 0}
                sx={{ textTransform: 'none' }}
              >
                {allSelectedInFolder ? 'Auswahl aufheben' : 'Alle markieren'}
              </Button>
              <IconButton
                size="small"
                onClick={(e) => openFolderMenu(e, node)}
                disabled={disabled}
                sx={{ color: 'text.secondary' }}
              >
                {node.action ? <CircularProgress size={16} /> : <MoreVert fontSize="small" />}
              </IconButton>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              {node.fileUrl && (
                <Button
                  size="small"
                  variant="text"
                  href={node.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download
                </Button>
              )}
              {!node.embedded && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleEmbed(node)}
                  disabled={disabled || (!node.fileUrl && !node.blobPath)}
                >
                  {node.action === 'embedding' ? 'Embedding…' : 'Embed'}
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => handleDelete(node)}
                disabled={disabled || (!node.fileUrl && !node.blobPath)}
                startIcon={node.action === 'deleting' ? <CircularProgress size={12} /> : <Delete fontSize="small" />}
              >
                Delete
              </Button>
            </Stack>
          )}
        </ListItem>

        {isFolder && (
          <Collapse in={node.expanded} timeout="auto" unmountOnExit={false}>
            <Box sx={{ pl: (depth + 1) * 2 }}>
              {node.loading && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Laden …
                  </Typography>
                </Stack>
              )}
              {node.error && !node.loading && (
                <Typography variant="caption" color="error" sx={{ py: 0.5 }}>
                  {node.error}
                </Typography>
              )}
              {node.children && node.children.length > 0 && (
                <List disablePadding>
                  {node.children.map((child) => renderNode(child, depth + 1))}
                </List>
              )}
              {node.children && node.children.length === 0 && !node.loading && (
                <Typography variant="caption" color="text.secondary" sx={{ py: 0.5 }}>
                  Leer
                </Typography>
              )}
            </Box>
          </Collapse>
        )}
      </React.Fragment>
    );
  }, [
    collectFilePaths,
    handleDelete,
    handleEmbed,
    handleToggle,
    openFolderMenu,
    selected,
    toggleFolderSelection,
    toggleSelect,
  ]);

  const content = useMemo(() => {
    if (rootLoading && nodes.length === 0) {
      return (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Laden …
          </Typography>
        </Stack>
      );
    }
    if (rootError) {
      return (
        <Typography variant="body2" color="error">
          {rootError}
        </Typography>
      );
    }
    if (nodes.length === 0) {
      return <EmptyState />;
    }
    return <List disablePadding>{nodes.map((n) => renderNode(n, 0))}</List>;
  }, [nodes, renderNode, rootError, rootLoading]);

  const menuNode = folderMenu ? findNodeByPath(nodes, folderMenu.path) : null;
  const menuDisabled = !menuNode || !!menuNode.action || menuNode.loading;

  return (
    <Box sx={(theme) => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      p: 2,
      backgroundColor: theme.palette.background.default,
    })}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Dateiverwaltung
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Ordner aufklappen lädt immer frisch nach
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {selected.size > 0 && (
            <Typography variant="caption" color="text.secondary">
              {selected.size} ausgewählt
            </Typography>
          )}
          {onCreateFolder && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleCreateFolder(null)}
              disabled={rootLoading}
              sx={{ textTransform: 'none' }}
            >
              Neuer Ordner
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            component="label"
            disabled={rootLoading}
          >
            Upload
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => {
                handleUploadToFolder(null, e.target.files);
                if (e.target.value) e.target.value = '';
              }}
            />
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            startIcon={<Delete fontSize="small" />}
            disabled={selected.size === 0}
            onClick={handleBulkDelete}
            sx={{ textTransform: 'none' }}
          >
            Ausgewählte löschen
          </Button>
          <IconButton onClick={loadRoot} disabled={rootLoading} sx={{ color: 'text.secondary' }}>
            {rootLoading ? <CircularProgress size={16} /> : <Refresh />}
          </IconButton>
        </Stack>
      </Box>
      {content}
      <Menu
        anchorEl={folderMenu?.anchorEl ?? null}
        open={!!folderMenu}
        onClose={closeFolderMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        {onCreateFolder && (
          <MenuItem
            onClick={() => {
              if (menuNode) {
                handleCreateFolder(menuNode);
              }
              closeFolderMenu();
            }}
            disabled={menuDisabled}
          >
            Neuer Ordner
          </MenuItem>
        )}
        <MenuItem component="label" disabled={menuDisabled}>
          Upload
          <input
            type="file"
            hidden
            multiple
            onChange={(e) => {
              if (menuNode) {
                handleUploadToFolder(menuNode, e.target.files);
              }
              closeFolderMenu();
              if (e.target.value) e.target.value = '';
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuNode) {
              handleRenameFolder(menuNode);
            }
            closeFolderMenu();
          }}
          disabled={menuDisabled}
        >
          Ordner umbenennen
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuNode) {
              handleDeleteFolder(menuNode);
            }
            closeFolderMenu();
          }}
          disabled={menuDisabled}
        >
          Ordner löschen
        </MenuItem>
      </Menu>
    </Box>
  );
};
